import React, { useContext, useRef, useEffect, useMemo, useState } from "react"
import { useFrame, useThree } from "react-three-fiber"
import * as THREE from "three"
import { ControllableContext } from "../../containers/Controllable"
import { GLTFModel } from "./GLTFModel"

const settings = {
  movementThreshold: 0.005, // Threshold to trigger controller movement
  movementIdle: 5, // time in seconds before controllers are set idle
  rayPosition: new THREE.Vector3(-0.01, -0.005, -0.52), // Position of the ray on the Oculus left controller
}

const Controllers = ({ cursor = true, session, onControllersIdle, onButtonsPressedUpdate, ...props }) => {
  const { gl } = useThree()

  const initialControllerState = [
    {
      controller: gl.xr.getController(0),
      gamepad: null,
      hand: "left",
      cursorRef: useRef(null),
    },
    {
      controller: gl.xr.getController(1),
      gamepad: null,
      hand: "right",
      cursorRef: useRef(null),
    },
  ]

  const [, updateState] = useState() // used for force re-render on controller connect
  const controllerMotion = useRef([])
  const motionIdle = useRef(false)
  const lastMotionTime = useRef(undefined)
  const frameCount = useRef(0)
  const controllers = useRef(initialControllerState)
  const buttonsPressed = useRef([[], []])
  const { interactable, input, setInput, onControllerUpdate } = useContext(ControllableContext)
  const tempMatrix = new THREE.Matrix4()
  const raycaster = new THREE.Raycaster()

  const rayTexture = useMemo(() => {
    const img = document.createElement("img")
    img.src =
      "data:image/png;base64,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"
    return img
  })

  const onConnect = (event) => {
    const controllerObject = controllers.current.filter(({ controller }) => controller === event.target)[0]
    controllerObject.gamepad = event.data.gamepad
    controllerObject.hand = event.data.handedness
    updateState({})
  }

  const onSelectStart = (event) => {
    const controllerObject = controllers.current.filter(({ controller }) => controller === event.target)[0]

    const { intersections, controller } = controllerObject

    if (!input.selecting) {
      setInput({
        selecting: true,
        intersection: intersections.length > 0 ? intersections[0] : null,
        controller,
      })
    }
  }

  const onSelectEnd = () => {
    setInput((prevState) => {
      return {
        selecting: false,
        controller: prevState.controller,
        intersection: prevState.intersection,
      }
    })
  }

  useEffect(() => {
    controllers.current.map(({ controller }) => {
      controller.addEventListener("connected", onConnect)
      controller.addEventListener("selectstart", onSelectStart)
      controller.addEventListener("selectend", onSelectEnd)
    })

    return () => {
      controllers.current.map(({ controller }) => {
        controller.removeEventListener("connected", onConnect)
        controller.removeEventListener("selectstart", onSelectStart)
        controller.removeEventListener("selectend", onSelectEnd)
      })
    }
  }, [])

  const getIntersections = (origin) => {
    // tracking issue #349
    // https://github.com/Immertec/medoptic-streamer/issues/349
    // https://sentry.io/organizations/immertec/issues/1443495274/?environment=production&project=1874505&query=is%3Aunresolved&sort=freq&statsPeriod=14d
    const filtered = interactable.current.filter(
      (object) => object.object3D !== null && object.object3D.geometry !== null,
    )
    const raycastObjects = filtered.map((object) => object.object3D)
    tempMatrix.identity().extractRotation(origin.matrixWorld)
    raycaster.ray.origin.setFromMatrixPosition(origin.matrixWorld)
    raycaster.ray.direction.set(0, 0, -1).applyMatrix4(tempMatrix)
    return raycaster.intersectObjects(raycastObjects)
  }

  const onFrame = ({ clock }) => {
    if (!session) {
      // no active XR session
      return
    }

    const shouldUpdateMotion = frameCount.current % 10 === 0
    const shouldCheckButtonsPressed = frameCount.current % 10 === 0
    frameCount.current++

    controllers.current.map(({ controller, gamepad, cursorRef }, index) => {
      const controllerIntersections = getIntersections(controller)

      // Make controller cursors visible over interactables.
      if (cursor && controllerIntersections.length > 0) {
        const intersection = controllerIntersections[0]
        cursorRef.current.position.set(0, 0, -intersection.distance + 0.05)
        cursorRef.current.lookAt(intersection.face.normal)
        cursorRef.current.visible = intersection.object.material.visible
      } else {
        cursorRef.current.visible = false
      }

      controllers.current[index].intersections = controllerIntersections
      onControllerUpdate(controller, controllerIntersections)

      // Check throughout pressed buttons
      let buttonsChanged = false
      if (gamepad && shouldCheckButtonsPressed) {
        for (let i = 0; i < gamepad.buttons.length; i++) {
          if (gamepad.buttons[i].pressed) {
            if (!buttonsPressed.current[index].includes(i)) {
              buttonsPressed.current[index].push(i)
              buttonsChanged = true
            }
          } else {
            if (buttonsPressed.current[index].includes(i)) {
              buttonsPressed.current[index] = buttonsPressed.current[index].filter((b) => b !== i)
              buttonsChanged = true
            }
          }
        }
      }
      if (buttonsChanged) {
        onButtonsPressedUpdate(buttonsPressed.current[index])
      }

      if (!shouldUpdateMotion) {
        return
      }

      // Calculate controller motion
      const motion = controller.position.toArray().reduce((a, b) => a + b, 0)
      const motionDelta =
        controllerMotion.current[index] !== undefined ? Math.abs(motion - controllerMotion.current[index]) : 0
      const hasMoved = motionDelta > settings.movementThreshold || input.selecting

      if (hasMoved) {
        lastMotionTime.current = clock.elapsedTime
      }

      controllerMotion.current[index] = motion
    })

    const motionTimeDelta = lastMotionTime.current !== undefined ? clock.elapsedTime - lastMotionTime.current : 0

    if (motionTimeDelta > settings.movementIdle) {
      if (!motionIdle.current) {
        // controllers idle
        motionIdle.current = true
        onControllersIdle(true)
      }
    } else {
      if (motionIdle.current) {
        // controllers moving
        motionIdle.current = false
        onControllersIdle(false)
      }
    }
  }

  useFrame(onFrame)

  return (
    <group {...props}>
      {controllers.current.map(({ controller, cursorRef, hand }) => {
        const rayColor = input.selecting && input.controller === controller ? 0x003eff : 0xffffff
        return (
          <primitive key={controller.uuid} object={controller}>
            <mesh
              position={[
                hand === "right" ? settings.rayPosition.x * -1 : settings.rayPosition.x,
                settings.rayPosition.y,
                settings.rayPosition.z,
              ]}
              scale={[0.1, 0.1, 0.1]}
              rotation={[-1.57, 0, 0]}
            >
              <boxBufferGeometry attach="geometry" args={[0.03, 10, 0.03]} />
              <meshBasicMaterial attach="material" color={rayColor} transparent>
                <texture attach="map" image={rayTexture} onUpdate={(self) => rayTexture && (self.needsUpdate = true)} />
              </meshBasicMaterial>
            </mesh>
            <mesh ref={cursorRef}>
              <circleBufferGeometry attach="geometry" args={[0.04, 8]} />
              <meshBasicMaterial attach="material" color={rayColor} />
            </mesh>
            {hand === "left" && (
              <GLTFModel path="/models/oculus-touch/" file="oculus-touch-controller-gen2-left.gltf" />
            )}
            {hand === "right" && (
              <GLTFModel path="/models/oculus-touch/" file="oculus-touch-controller-gen2-right.gltf" />
            )}
          </primitive>
        )
      })}
    </group>
  )
}

export default Controllers
